import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { TrussSelector } from './TrussSelector';
import { MaterialSelector } from './MaterialSelector';
import { RailingSelector } from './RailingSelector';
import { DeckingSelector } from './DeckingSelector';
import { AddOnsSelector } from './AddOnsSelector';
import { BridgeViewer3D } from './BridgeViewer3D';
import { BridgeLengthInput } from './BridgeLengthInput';
import { BridgeWidthInput } from './BridgeWidthInput';
import { BridgeCamberInput } from './BridgeCamberInput';
import { PriceCalculator } from './PriceCalculator';
import { QuoteRequestForm } from './QuoteRequestForm';
import { WeightEstimator } from './WeightEstimator';
import BudgetContact from './BudgetContact';
import { defaultConfiguration, calculateBridgePrice, coatingOptions, woodTypeOptions, concreteAdderOptions, steelDecorOptions, picketStyleOptions, wingwallOptions, loadRatingOptions } from '../data/mockData';
import { Download, Save } from 'lucide-react';
import { Ruler, ArrowsOutSimple as Maximize2, ArrowBendUpRight as TrendingUp, ArrowsClockwise as RefreshCw, Wrench, ShieldCheck, ClipboardText, Sparkle, GridFour } from 'phosphor-react';
import { formatArea, formatCamber, formatLength } from '../utils/units';
import { DisclaimerModal, DisclaimerFooter } from './DisclaimerModal';

export const BridgeBuilder = ({ embedMode = false, brandLogoUrl = '', mailTo = '' }) => {
  const [configuration, setConfiguration] = useState(defaultConfiguration);
  const [activePanel, setActivePanel] = useState('truss');
  const [isGenerating, setIsGenerating] = useState(false);
  const [pricing, setPricing] = useState(calculateBridgePrice(defaultConfiguration));
  const [units, setUnits] = useState('imperial'); // 'imperial' | 'metric'
  const [contactUnlocked, setContactUnlocked] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);


  const updateConfiguration = (key, value) => {
    const newConfig = {
      ...configuration,
      [key]: value
    };
    setConfiguration(newConfig);
    setPricing(calculateBridgePrice(newConfig));
    setIsGenerating(true);
    setTimeout(() => setIsGenerating(false), 800);
  };

  const updateSubModifiers = (patch) => {
    const newSub = { ...(configuration.subModifiers || {}), ...patch };
    updateConfiguration('subModifiers', newSub);
  };

  const panels = [
    { id: 'length', label: 'Length', icon: <Ruler size={16} /> },
    { id: 'width', label: 'Width', icon: <Maximize2 size={16} /> },
    { id: 'camber', label: 'Camber', icon: <TrendingUp size={16} /> },
    { id: 'truss', label: 'Truss Type', icon: <GridFour size={16} /> },
    { id: 'material', label: 'Materials', icon: <Wrench size={16} /> },
    { id: 'railing', label: 'Railing', icon: <ShieldCheck size={16} /> },
    { id: 'decking', label: 'Decking', icon: <ClipboardText size={16} /> },
    { id: 'addons', label: 'Add-Ons', icon: <Sparkle size={16} /> }
  ];

  const isConcreteDeck = configuration.decking === 'concrete';
  const isWoodDeck = configuration.decking === 'wood';
  const isVerticalRailing = configuration.railing === 'vertical';

  return (
    <div className="pro-theme page-bg min-h-screen">
      {/* Disclaimer popup on first visit */}
      <DisclaimerModal />
      {/* Disclaimer re-opened from footer */}
      {showDisclaimer && (
        <DisclaimerModal forceOpen onClose={() => setShowDisclaimer(false)} />
      )}
      {/* Unified header: 365 logo/title left, Coyote Bridges logo right on translucent grey square */}
      <header className={`glass-header text-white ${embedMode ? 'py-4 px-6' : 'p-6'} shadow-2xl`}>
        <div className="container mx-auto grid grid-cols-3 items-center">
          {/* Left: 365 logo */}
          <div className="flex items-center justify-start gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_truss-visualizer/artifacts/pdjbjpx3_2020%20365isi%20Logo%20-White%20Text.png"
              alt="365 Industrial Solutions"
              className="h-10 w-auto"
            />
          </div>

          {/* Center: Title + subtitle, truly centered */}
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl font-bold">Interactive Bridge Builder</h1>
            <p className="text-slate-300 text-xs sm:text-sm">Design and visualize your custom bridge in real-time</p>
          </div>

          {/* Right: Coyote Bridges logo on translucent square */}
          <div className="flex items-center justify-end">
            <div className="bg-white/12 border border-white/20 rounded-md p-2">
              <img src={brandLogoUrl || 'https://customer-assets.emergentagent.com/job_bridgevis/artifacts/synnsd1q_trans%20logo.png'} alt="Coyote Bridges" className="h-10 w-auto" />
            </div>
          </div>
        </div>
      </header>

      {/* Units toggle only when not embedded */}
      {!embedMode && (
        <div className="container mx-auto px-6 mt-2 flex justify-end">
          <div className="segmented flex items-center text-xs rounded-full overflow-hidden">
            <button
              className={`px-3 py-1 ${units === 'imperial' ? 'active' : ''}`}
              onClick={() => setUnits('imperial')}
            >
              Imperial
            </button>
            <button
              className={`px-3 py-1 ${units === 'metric' ? 'active' : ''}`}
              onClick={() => setUnits('metric')}
            >
              Metric
            </button>
          </div>
        </div>
      )}

      <div className="container mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-full">
          {/* If contact not unlocked, show overlay on price */}
          <script dangerouslySetInnerHTML={{ __html: `
            (function(){
              try {
                var overlay = document.getElementById('price-overlay');
                if (overlay && !${'contactUnlocked'}) { overlay.classList.remove('hidden'); }
              } catch(e){}
            })();
          ` }} />
          {/* Configuration Panel */}
          <div className="lg:col-span-1 space-y-4">
            <Card className="glass-card shadow-xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-slate-100">Configuration</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  {panels.map((panel) => (
                    <Button
                      key={panel.id}
                      variant={activePanel === panel.id ? 'default' : 'outline'}
                      className={`brand-pill ${activePanel === panel.id ? 'brand-pill-active' : ''} text-sm p-3 h-auto flex flex-col items-center gap-1`}
                      onClick={() => setActivePanel(panel.id)}
                    >
                      <span className="text-lg">{panel.icon}</span>
                      <span className="text-xs">{panel.label}</span>
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {activePanel !== 'length' && activePanel !== 'width' && activePanel !== 'camber' && (
              <Card className="glass-card shadow-xl">
                <CardContent className="p-4">
                  {(() => {
                    switch (activePanel) {
                      case 'truss':
                        return <TrussSelector selected={configuration.trussType} onSelect={(v) => updateConfiguration('trussType', v)} />;
                      case 'material':
                        return <MaterialSelector selected={configuration.material} onSelect={(v) => updateConfiguration('material', v)} />;
                      case 'railing':
                        return <RailingSelector selected={configuration.railing} onSelect={(v) => updateConfiguration('railing', v)} />;
                      case 'decking':
                        return <DeckingSelector selected={configuration.decking} onSelect={(v) => updateConfiguration('decking', v)} />;
                      case 'addons':
                        return <AddOnsSelector selected={configuration.addOns} onSelect={(v) => updateConfiguration('addOns', v)} />;
                      default:
                        return null;
                    }
                  })()}
                </CardContent>
              </Card>
            )}

            {(activePanel === 'length' || activePanel === 'width' || activePanel === 'camber') && (
              <div>
                {activePanel === 'length' && (
                  <BridgeLengthInput length={configuration.length} onLengthChange={(v) => updateConfiguration('length', v)} units={units} />
                )}
                {activePanel === 'width' && (
                  <BridgeWidthInput width={configuration.width} onWidthChange={(v) => updateConfiguration('width', v)} units={units} />
                )}
                {activePanel === 'camber' && (
                  <BridgeCamberInput camber={configuration.camber} onCamberChange={(v) => updateConfiguration('camber', v)} units={units} />
                )}
              </div>
            )}

            <Card className="glass-card shadow-xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-slate-100 text-lg">Current Configuration</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-400">Length:</span>
                    <Badge className="bg-blue-600 text-white">{formatLength(configuration.length, units)}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-400">Width:</span>
                    <Badge className="bg-blue-600 text-white">{formatLength(configuration.width, units)}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-400">Camber:</span>
                    <Badge className={`${configuration.camber === 0 ? 'bg-slate-600' : 'bg-purple-600'} text-white`}>
                      {formatCamber(configuration.camber, units)}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-400">Area:</span>
                    <Badge className="bg-emerald-600 text-white">{formatArea(configuration.length, configuration.width, units)}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-400">Truss Type:</span>
                    <Badge className="bg-blue-600 text-white">{configuration.trussType}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-400">Material:</span>
                    <Badge className="bg-blue-600 text-white">{configuration.material}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-400">Railing:</span>
                    <Badge className="bg-blue-600 text-white">{configuration.railing}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-400">Decking:</span>
                    <Badge className="bg-blue-600 text-white">{configuration.decking}</Badge>
                  </div>
                  {configuration.addOns.length > 0 && (
                    <div className="flex justify-between items-start">
                      <span className="text-sm text-slate-400">Add-Ons:</span>
                      <div className="flex flex-wrap gap-1">
                        {configuration.addOns.map((addon) => (
                          <Badge key={addon} className="bg-emerald-600 text-white text-xs">
                            {addon}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="pt-2 grid grid-cols-2 gap-2">
                    <Badge className="bg-slate-600 text-slate-100">Coating: {configuration.subModifiers?.coating}</Badge>
                    {configuration.decking === 'wood' && (
                      <Badge className="bg-slate-600 text-slate-100">Wood: {configuration.subModifiers?.woodType}</Badge>
                    )}
                    {configuration.railing === 'vertical' && (
                      <Badge className="bg-slate-600 text-slate-100">Pickets: {configuration.subModifiers?.picketStyle}</Badge>
                    )}
                    <Badge className="bg-slate-600 text-slate-100">Load: {(() => {
                      const lr = loadRatingOptions.find(o => o.id === configuration.subModifiers?.loadRating);
                      return lr ? lr.name : '—';
                    })()}</Badge>
                    {configuration.subModifiers?.wingwalls !== 'none' && (
                      <Badge className="bg-slate-600 text-slate-100">Wingwalls: {configuration.subModifiers?.wingwalls}</Badge>
                    )}
                    {configuration.subModifiers?.wingwallRailings && (
                      <Badge className="bg-slate-600 text-slate-100">Wingwall Railings</Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-2">
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg"
                disabled={isGenerating}
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Generating Model...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Regenerate Model
                  </>
                )}
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 border-slate-600 bg-slate-700 text-slate-200 hover:bg-slate-600">
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
                <Button variant="outline" className="flex-1 border-slate-600 bg-slate-700 text-slate-200 hover:bg-slate-600">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-4">
            <Card className="h-[600px] glass-card shadow-xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-slate-100">3D Bridge Model</CardTitle>
              </CardHeader>
              <CardContent className="h-[calc(100%-4rem)]">
                <BridgeViewer3D 
                  configuration={configuration}
                  isGenerating={isGenerating}
                  units={units}
                />
              </CardContent>
            </Card>

            <Card className="glass-card shadow-xl">
              <CardContent className="p-4">
                <QuoteRequestForm configuration={configuration} pricing={pricing} onFormSubmitted={() => setContactUnlocked(true)}>
                  <Button data-testid="formal-request-btn" className="w-full brand-gradient cta-shimmer hover:opacity-95 text-white font-extrabold py-6 text-xl rounded-2xl shadow-2xl ring-1 ring-[rgba(137,185,237,0.35)]">
                    I'd Like to Submit a Formal Request
                  </Button>
                </QuoteRequestForm>
                <p className="mt-2 text-center text-xs text-slate-400">No obligation. We’ll reply within 24 hours.</p>
              </CardContent>
            </Card>

            <Card className="glass-card shadow-xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-slate-100 text-lg">Advanced Configuration</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <Label className="text-slate-300 text-sm font-medium">Coating/Finish</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {coatingOptions.map(opt => (
                        <Button key={opt.id} size="sm" variant={configuration.subModifiers?.coating === opt.id ? 'default' : 'outline'}
                          className={`${configuration.subModifiers?.coating === opt.id ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-600 bg-slate-700 text-slate-300'} h-8 text-xs`}
                          onClick={() => updateSubModifiers({ coating: opt.id })}>
                          {opt.name}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-slate-300 text-sm font-medium flex items-center gap-2">Wood Type { configuration.decking !== 'wood' && <span className="text-[10px] text-slate-400">(select Wood decking to apply)</span> }</Label>
                    <div className="mt-2 flex flex-wrap gap-2 opacity-100">
                      {woodTypeOptions.map(opt => (
                        <Button key={opt.id} size="sm" disabled={configuration.decking !== 'wood'} variant={configuration.subModifiers?.woodType === opt.id ? 'default' : 'outline'}
                          className={`${configuration.subModifiers?.woodType === opt.id ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-600 bg-slate-700 text-slate-300'} h-8 text-xs disabled:opacity-50`}
                          onClick={() => configuration.decking === 'wood' && updateSubModifiers({ woodType: opt.id })}>
                          {opt.name}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-slate-300 text-sm font-medium flex items-center gap-2">Special Concrete Adders { configuration.decking !== 'concrete' && <span className="text-[10px] text-slate-400">(select Concrete decking to apply)</span> }</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {concreteAdderOptions.map(opt => {
                        const current = configuration.subModifiers?.concreteAdders || {};
                        const selected = !!current[opt.id];
                        return (
                          <Button key={opt.id} size="sm" disabled={configuration.decking !== 'concrete'} variant={selected ? 'default' : 'outline'}
                            className={`${selected ? 'bg-emerald-600 text-white border-emerald-600' : 'border-slate-600 bg-slate-700 text-slate-300'} h-8 text-xs disabled:opacity-50`}
                            onClick={() => {
                              if (configuration.decking !== 'concrete') return;
                              updateSubModifiers({ concreteAdders: { ...current, [opt.id]: !selected } });
                            }}>
                            {opt.name}
                          </Button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <Label className="text-slate-300 text-sm font-medium">Decorative Steel Add-ons</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {steelDecorOptions.map(opt => {
                        const list = configuration.subModifiers?.steelDecor || [];
                        const selected = list.includes(opt.id);
                        return (
                          <Button key={opt.id} size="sm" variant={selected ? 'default' : 'outline'}
                            className={`${selected ? 'bg-emerald-600 text-white border-emerald-600' : 'border-slate-600 bg-slate-700 text-slate-300'} h-8 text-xs`}
                            onClick={() => {
                              const next = selected ? list.filter(i => i !== opt.id) : [...list, opt.id];
                              updateSubModifiers({ steelDecor: next });
                            }}>
                            {opt.name}
                          </Button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <Label className="text-slate-300 text-sm font-medium flex items-center gap-2">Fancy Pickets { configuration.railing !== 'vertical' && <span className="text-[10px] text-slate-400">(select Vertical railing to apply)</span> }</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {picketStyleOptions.map(opt => (
                        <Button key={opt.id} size="sm" disabled={configuration.railing !== 'vertical'} variant={configuration.subModifiers?.picketStyle === opt.id ? 'default' : 'outline'}
                          className={`${configuration.subModifiers?.picketStyle === opt.id ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-600 bg-slate-700 text-slate-300'} h-8 text-xs disabled:opacity-50`}
                          onClick={() => configuration.railing === 'vertical' && updateSubModifiers({ picketStyle: opt.id })}>
                          {opt.name}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-slate-300 text-sm font-medium">Load Rating</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {loadRatingOptions.map(opt => (
                        <Button key={opt.id} size="sm" variant={configuration.subModifiers?.loadRating === opt.id ? 'default' : 'outline'}
                          className={`${configuration.subModifiers?.loadRating === opt.id ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-600 bg-slate-700 text-slate-300'} h-8 text-xs`}
                          onClick={() => updateSubModifiers({ loadRating: opt.id })}>
                          {opt.name}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-slate-300 text-sm font-medium">Wingwalls</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {wingwallOptions.map(opt => (
                        <Button key={opt.id} size="sm" variant={configuration.subModifiers?.wingwalls === opt.id ? 'default' : 'outline'}
                          className={`${configuration.subModifiers?.wingwalls === opt.id ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-600 bg-slate-700 text-slate-300'} h-8 text-xs`}
                          onClick={() => updateSubModifiers({ wingwalls: opt.id })}>
                          {opt.name}
                        </Button>
                      ))}
                      <Button size="sm" disabled={configuration.subModifiers?.wingwalls === 'none'} variant={configuration.subModifiers?.wingwallRailings ? 'default' : 'outline'}
                        className={`${configuration.subModifiers?.wingwallRailings ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-600 bg-slate-700 text-slate-300'} h-8 text-xs`}
                        onClick={() => updateSubModifiers({ wingwallRailings: !configuration.subModifiers?.wingwallRailings })}>
                        Wingwall Railings
                      </Button>
                    </div>
                    {configuration.subModifiers?.wingwalls !== 'none' && (
                      <div className="mt-3 flex items-end gap-2">
                        <Label className="text-slate-300 text-xs">Wingwall Length ({units === 'metric' ? 'm' : 'ft'})</Label>
                        <input
                          type="number"
                          className="h-8 w-28 rounded-md bg-slate-700 border border-slate-600 px-2 text-slate-200 text-sm"
                          placeholder={units === 'metric' ? 'auto' : 'auto'}
                          value={(() => {
                            const valFt = configuration.subModifiers?.wingwallLengthFt;
                            if (!valFt) return '';
                            return units === 'metric' ? (valFt * 0.3048).toFixed(2) : String(valFt);
                          })()}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === '') return updateSubModifiers({ wingwallLengthFt: null });
                            const num = parseFloat(raw);
                            if (isNaN(num)) return;
                            const valFt = units === 'metric' ? (num / 0.3048) : num;
                            updateSubModifiers({ wingwallLengthFt: valFt });
                          }}
                          min={units === 'metric' ? '1.00' : '3'}
                          max={units === 'metric' ? '12.00' : '40'}
                          step={units === 'metric' ? '0.10' : '1'}
                        />
                        <span className="text-slate-500 text-xs">Leave blank for auto</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Price Calculator + Weight + Budget Capture */}
          <div className="lg:col-span-1 space-y-4">
            {/* Budget price gating: show lock card until contact is provided */}
            {contactUnlocked ? (
              <PriceCalculator configuration={configuration} onRequestQuote={() => {}} showCTA={false} units={units} />
            ) : (
              <Card className="glass-card shadow-xl">
                <CardHeader className="pb-3">
                  <CardTitle className="text-slate-100 text-lg">Budget Estimate Locked</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-slate-300 text-sm">
                    Enter your contact info below to reveal your budget estimate.
                  </div>
                </CardContent>
              </Card>
            )}

            <BudgetContact 
              configuration={configuration}
              pricing={pricing}
              units={units}
              mailTo={mailTo}
              onUnlock={() => {
                setContactUnlocked(true);
              }}
            />

            <WeightEstimator configuration={configuration} units={units} />
          </div>
        </div>
      </div>

      {/* Persistent disclaimer footer */}
      <DisclaimerFooter onClick={() => setShowDisclaimer(true)} />
    </div>
  );
};
